import { useCallback, useEffect, useState } from 'react'

import { SelectionColor } from '~/bootcamp/Activities/Algebra/Components/SquareColorPicker'

import { generateEquationForPowerChoice, EquationPartsModel, OptionModel } from './PowerChoice.utils'

interface UsePowerChoiceReturnType {
  equationParts?: EquationPartsModel[]
  options: OptionModel[]
  answerIndex: number
  formValid: boolean
  handleCheckAnswer: () => void
  onInputChange: (val: number, color: SelectionColor) => void
  goToNextQuestion: () => void
  clearForm: () => void
}

interface UsePowerChoiceProps {
  difficultyLevel: number
  onNextQuestionClick: () => void
  onAnswerStateChange: (value: boolean | null) => void
}

function UsePowerChoice({
  onAnswerStateChange,
  onNextQuestionClick,
  difficultyLevel,
}: UsePowerChoiceProps): UsePowerChoiceReturnType {
  const [taskHistory, setTaskHistory] = useState<string[]>([])
  const [equationParts, setEquationParts] = useState<EquationPartsModel[]>([])
  const [options, setOptions] = useState<OptionModel[]>([])
  const [answerIndex, setAnswerIndex] = useState<number>(-1)
  const [equationString, setEquationString] = useState('')

  useEffect(() => {
    setAnswerIndex(-1)
    const [_equationParts, _equationString, _options] = generateEquationForPowerChoice(difficultyLevel, taskHistory)
    setEquationParts(_equationParts)
    setEquationString(_equationString)
    setOptions(_options)
  }, [taskHistory, difficultyLevel])

  const handleCheckAnswer = useCallback((): void => {
    let isAnswerRight = false
    options.forEach(({ isRight }, index) => {
      if (isRight && index === answerIndex) isAnswerRight = true
    })
    onAnswerStateChange(isAnswerRight)
  }, [onAnswerStateChange, answerIndex, options])

  const onInputChange = useCallback(
    (index: number) => {
      setAnswerIndex(index)
      onAnswerStateChange(null)
    },
    [onAnswerStateChange],
  )

  const clearForm = useCallback(() => {
    onInputChange(-1)
  }, [onInputChange])

  const goToNextQuestion = useCallback(() => {
    setTaskHistory((prevState) => [equationString, ...prevState].slice(0, 3))
    onNextQuestionClick()
  }, [onNextQuestionClick, equationString])

  return {
    equationParts,
    options,
    answerIndex,
    formValid: true,
    handleCheckAnswer,
    onInputChange,
    goToNextQuestion,
    clearForm,
  }
}

export default UsePowerChoice
